import { useRef } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { addDaysToDate, parseAsLocalDate } from "../../../utils";
import { UTSAV_THEME } from "../theme.config";
import { coupleNames, heroIllustration } from "../helpers";
import Toran from "../art/Garland";
import { Butterfly } from "../art/Icons";
import { Petals, Sparkles } from "../art/Ambience";
import KeyedVideo from "../art/KeyedVideo";
import useLive from "../useLive";
import SacredMark from "./SacredMark";
// The courtyard: a flower-covered arch with cushions, bushes, umbrellas, dholaks and a
// rug, assembling itself on a black backdrop (keyed out in art/KeyedVideo.js) ...
import heroVideo from "../../../animated-elements/rang-utsav-hero.mp4";
// ... and the same courtyard as a still, for when the video can't play.
import heroStill from "../../../images/rang-utsav/rang-utsav-hero.webp";
import ScrollCue from "../../ScrollCue/ScrollCue";
import { coupleLanding } from "./CoupleFlight";

// How long the "Scroll down" prompt takes to carry the guest to the couple page, slow
// enough to watch the couple fly there from the courtyard (CoupleFlight)
const GLIDE_TO_COUPLE_MS = 2400;

const BUTTERFLIES = [
  { className: "ut-hero__butterfly--1", colors: ["#f06292", "#ffb300"] },
  { className: "ut-hero__butterfly--2", colors: ["#26a69a", "#7e57c2"] },
  { className: "ut-hero__butterfly--3", colors: ["#ffb300", "#e2457f"] },
];

/** Splits a name into letters so each one can rise in turn. */
const Letters = ({ text, start }) => (
  <span className="ut-hero__name" aria-label={text}>
    {[...text].map((char, i) => (
      <span key={i} aria-hidden="true" style={{ "--i": start + i }}>
        {char}
      </span>
    ))}
  </span>
);

/**
 * The illustration standing under the flower arch (helpers.heroIllustration). Until
 * one is added, the arch stands on its own; in development a dashed outline marks the spot.
 */
const Illustration = ({ src }) => {
  if (src) return <img className="ut-hero__illustration" src={src} alt="The bride and groom" decoding="async" />;
  if (process.env.NODE_ENV === "production") return null;
  return (
    <div className="ut-hero__illustration ut-hero__illustration--placeholder" aria-hidden="true">
      <span>Couple illustration</span>
      <small>src/images/rang-utsav/hero-illustration.webp</small>
    </div>
  );
};

/**
 * The first page: a marigold toran across the top, the blessing, the couple's names
 * and the date on the same paper as the other pages, and below them the courtyard
 * assembling itself (the video plays once and holds its last frame), with the couple's
 * illustration under its flower arch. Petals fall, gold sparkles twinkle and butterflies
 * wander over it. While the opening gate is still shut (`revealed` false) the courtyard
 * waits, so the guest sees it assemble.
 */
const Hero = ({ invite, isPreview, revealed = true }) => {
  const ref = useRef(null);
  useLive(ref);
  const { first, second } = coupleNames(invite?.bride, invite?.groom);
  const date = isPreview ? addDaysToDate(16) : parseAsLocalDate(invite?.eventDate);
  // addDaysToDate returns a date string; wrap in Date so moment doesn't parse a non-ISO string.
  const day = date ? moment(new Date(date)) : null;
  const illustration = heroIllustration(invite);

  return (
    <section ref={ref} className="ut-hero" aria-label="Wedding invitation">
      <Toran className="ut-hero__toran" />
      <Sparkles className="ut-hero__sparkles" />
      <Petals className="ut-hero__petals" />

      <div className="ut-hero__content">
        <SacredMark className="ut-hero__emblem" />
        <p className="ut-hero__devanagari" aria-label={UTSAV_THEME.heroTitleLabel}>
          <span aria-hidden="true">{UTSAV_THEME.heroTitle}</span>
        </p>
        <p className="ut-kicker ut-hero__intro">Together with our families, we joyfully invite you to celebrate the wedding of</p>
        <h1 className="ut-hero__names">
          <Letters text={first} start={0} />
          <span className="ut-script ut-hero__amp">&amp;</span>
          <Letters text={second} start={first.length + 2} />
        </h1>
        {day && (
          <div className="ut-hero__date" aria-label={day.format("dddd, D MMMM YYYY")}>
            <span className="ut-hero__date-side" aria-hidden="true">
              {day.format("dddd")}
            </span>
            <span className="ut-hero__date-day" aria-hidden="true">
              {day.format("DD")}
            </span>
            <span className="ut-hero__date-side" aria-hidden="true">
              {day.format("MMMM")}
            </span>
            <span className="ut-kicker ut-hero__date-year" aria-hidden="true">
              {day.format("YYYY")}
            </span>
          </div>
        )}
      </div>

      <div className="ut-hero__stage">
        <KeyedVideo className="ut-hero__stage-media" src={heroVideo} fallback={heroStill} play={revealed} />
        <Illustration src={illustration} />
      </div>

      {BUTTERFLIES.map((b) => (
        <div key={b.className} className={`ut-hero__butterfly ${b.className}`} aria-hidden="true">
          <Butterfly colors={b.colors} />
        </div>
      ))}
      <ScrollCue scrollTarget={coupleLanding} glideMs={GLIDE_TO_COUPLE_MS} />
    </section>
  );
};

const mapStateToProps = (state) => ({
  invite: state.inviteDetails.invite,
  isPreview: state.inviteDetails?.isPreview,
});

export default connect(mapStateToProps)(Hero);
